import type { ParsedCommand, CommandType, VoiceCommand } from '../types/voice';

// Command definitions with aliases
export const voiceCommands: VoiceCommand[] = [
  // Navigation commands
  { id: 'nav-chat', command: 'go to chat', aliases: ['ir al chat', 'chat'], action: 'focusChat', type: 'navigation', requiresConfirmation: false },
  { id: 'nav-avatar', command: 'show avatar', aliases: ['mostrar avatar', 'avatar'], action: 'showAvatar', type: 'navigation', requiresConfirmation: false },
  { id: 'nav-messages', command: 'show messages', aliases: ['mostrar mensajes', 'mensajes'], action: 'showMessages', type: 'navigation', requiresConfirmation: false },
  { id: 'nav-fullscreen', command: 'fullscreen', aliases: ['pantalla completa', 'full screen'], action: 'toggleFullscreen', type: 'navigation', requiresConfirmation: false },
  
  // Chat commands
  { id: 'chat-send', command: 'send', aliases: ['enviar', 'mandar', 'envía', 'submit'], action: 'sendMessage', type: 'chat', requiresConfirmation: false },
  { id: 'chat-clear', command: 'clear', aliases: ['borrar', 'limpiar', 'delete', 'eliminar'], action: 'clearInput', type: 'chat', requiresConfirmation: false },
  { id: 'chat-clear-all', command: 'clear all', aliases: ['borrar todo', 'limpiar todo', 'clear history'], action: 'clearHistory', type: 'chat', requiresConfirmation: true },
  { id: 'chat-repeat', command: 'repeat', aliases: ['repetir', 'say again', 'repítelo'], action: 'repeatLast', type: 'chat', requiresConfirmation: false },
  { id: 'chat-copy', command: 'copy last', aliases: ['copiar último', 'copy'], action: 'copyLast', type: 'chat', requiresConfirmation: false },
  
  // Audio commands
  { id: 'audio-mute', command: 'mute', aliases: ['silencio', 'callar', 'be quiet', 'silence'], action: 'muteAudio', type: 'audio', requiresConfirmation: false },
  { id: 'audio-unmute', command: 'unmute', aliases: ['activar sonido', 'speak', 'sound on'], action: 'unmuteAudio', type: 'audio', requiresConfirmation: false },
  { id: 'audio-volume-up', command: 'volume up', aliases: ['subir volumen', 'louder'], action: 'volumeUp', type: 'audio', requiresConfirmation: false },
  { id: 'audio-volume-down', command: 'volume down', aliases: ['bajar volumen', 'quieter'], action: 'volumeDown', type: 'audio', requiresConfirmation: false },
  { id: 'audio-stop', command: 'stop', aliases: ['detener', 'parar', 'halt', 'basta'], action: 'stopAudio', type: 'audio', requiresConfirmation: false },
  
  // Avatar commands
  { id: 'avatar-reset', command: 'reset view', aliases: ['reiniciar vista', 'reset camera'], action: 'resetView', type: 'avatar', requiresConfirmation: false },
  { id: 'avatar-zoom-in', command: 'zoom in', aliases: ['acercar', 'zoom'], action: 'zoomIn', type: 'avatar', requiresConfirmation: false },
  { id: 'avatar-zoom-out', command: 'zoom out', aliases: ['alejar'], action: 'zoomOut', type: 'avatar', requiresConfirmation: false },
  { id: 'avatar-rotate', command: 'rotate', aliases: ['rotar', 'auto rotate'], action: 'toggleRotate', type: 'avatar', requiresConfirmation: false },
  
  // App commands
  { id: 'app-help', command: 'help', aliases: ['ayuda', 'commands', 'comandos'], action: 'showHelp', type: 'app', requiresConfirmation: false },
  { id: 'app-settings', command: 'settings', aliases: ['configuración', 'config', 'preferences'], action: 'openSettings', type: 'app', requiresConfirmation: false },
  { id: 'app-close', command: 'close', aliases: ['cerrar', 'exit', 'salir'], action: 'closePanel', type: 'app', requiresConfirmation: false },
  { id: 'app-refresh', command: 'refresh', aliases: ['actualizar', 'reload'], action: 'refreshChat', type: 'app', requiresConfirmation: false },
];

// Wake words
const wakeWords = ['hey avatar', 'hola avatar', 'avatar', 'ok avatar'];

/**
 * Parse a transcript to determine if it's a command or text
 */
export function parseCommand(transcript: string): ParsedCommand {
  const normalizedText = transcript.toLowerCase().trim();
  
  // Check for wake word and remove it
  let commandText = normalizedText;
  let hasWakeWord = false;
  
  for (const wakeWord of wakeWords) {
    if (normalizedText.startsWith(wakeWord)) {
      commandText = normalizedText.replace(wakeWord, '').trim();
      hasWakeWord = true;
      break;
    }
  }
  
  // Try to match against known commands
  for (const command of voiceCommands) {
    // Check exact command match
    if (commandText === command.command.toLowerCase()) {
      return {
        type: command.type,
        action: command.action,
        confidence: 0.95,
        originalText: transcript,
      };
    }
    
    // Check aliases
    for (const alias of command.aliases) {
      if (commandText === alias.toLowerCase() || 
          (hasWakeWord && commandText.includes(alias.toLowerCase()))) {
        return {
          type: command.type,
          action: command.action,
          confidence: 0.9,
          originalText: transcript,
        };
      }
    }
    
    // Check if command is contained in text (for natural language)
    if (commandText.includes(command.command.toLowerCase())) {
      return {
        type: command.type,
        action: command.action,
        confidence: 0.75,
        originalText: transcript,
      };
    }
  }
  
  // No command matched, treat as text
  return {
    type: 'text',
    confidence: 1.0,
    originalText: transcript,
  };
}

/**
 * Get command by action name
 */
export function getCommandByAction(action: string): VoiceCommand | undefined {
  return voiceCommands.find(cmd => cmd.action === action);
}

/**
 * Get all commands by type
 */
export function getCommandsByType(type: CommandType): VoiceCommand[] {
  return voiceCommands.filter(cmd => cmd.type === type);
}

/**
 * Format command for display
 */
export function formatCommand(command: VoiceCommand, language: string = 'es'): string {
  if (language === 'es') {
    // Return Spanish alias if available
    const spanishAlias = command.aliases.find(a => 
      ['ir', 'mostrar', 'enviar', 'borrar', 'silencio', 'detener', 'reiniciar', 'acercar', 'alejar', 'ayuda', 'configuración', 'cerrar', 'actualizar'].some(prefix => 
        a.toLowerCase().startsWith(prefix)
      )
    );
    return spanishAlias || command.command;
  }
  return command.command;
}